/**
 * Pre-sign-out hook registry.
 *
 * Lets a provider nested UNDER AuthProvider (e.g. OrgProvider) contribute async
 * cleanup that must run BEFORE Supabase clears the auth session on sign-out —
 * for example, closing an authoritative server-side support view session while
 * the access token is still valid. Kept as a standalone module so AuthProvider
 * and OrgProvider share it without importing one another (no circular
 * dependency).
 */
type PreSignoutHook = () => Promise<void>;

const hooks = new Set<PreSignoutHook>();

/** Register a hook; returns an unregister function for effect cleanup. */
export function registerPreSignoutHook(hook: PreSignoutHook): () => void {
  hooks.add(hook);
  return () => {
    hooks.delete(hook);
  };
}

/**
 * Run every registered hook, isolating failures so one hook can neither block
 * sign-out nor throw into the caller. Returns the errors observed so the caller
 * can inspect/log them rather than falsely assuming every hook succeeded.
 */
export async function runPreSignoutHooks(): Promise<unknown[]> {
  const errors: unknown[] = [];
  await Promise.all(
    [...hooks].map(async (hook) => {
      try {
        await hook();
      } catch (error) {
        errors.push(error);
      }
    }),
  );
  return errors;
}

/** Test/reset helper: clears all registered hooks. */
export function __resetPreSignoutHooks(): void {
  hooks.clear();
}
